import { useEffect, useRef, useState } from 'react';
import { saveSchoolLogo } from './mockLogoSave';

interface LogoAsset { url: string; width: number; height: number }

export function useSchoolLogo(report: (message: string) => void) {
  const [asset, setAsset] = useState<LogoAsset | null>(null);
  const [confirmed, setConfirmed] = useState<LogoAsset | null>(null);
  const [status, setStatus] = useState<'idle' | 'reading' | 'saving' | 'saved' | 'failed'>('idle');
  const draft = useRef<LogoAsset | null>(null);
  const saved = useRef<LogoAsset | null>(null);
  const active = useRef<symbol | null>(null);
  const reader = useRef<FileReader | null>(null);
  const controller = useRef<AbortController | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted = useRef(true);

  function cancel() {
    active.current = null;
    reader.current?.abort(); reader.current = null;
    controller.current?.abort(); controller.current = null;
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  }

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; cancel(); };
  }, []);

  async function persist(next: LogoAsset | null) {
    cancel();
    const job = Symbol('logo-save');
    const request = new AbortController();
    active.current = job;
    controller.current = request;
    setStatus('saving');
    try {
      await saveSchoolLogo(next?.url ?? '', request.signal);
      if (!mounted.current || active.current !== job) return;
      saved.current = next;
      setConfirmed(next);
      active.current = null; controller.current = null;
      setStatus('saved');
      timer.current = setTimeout(() => {
        if (mounted.current && draft.current === next) setStatus('idle');
        timer.current = null;
      }, 2000);
    } catch {
      if (!mounted.current || active.current !== job) return;
      active.current = null; controller.current = null;
      setStatus('failed');
    }
  }

  function select(files: readonly File[]) {
    const file = files[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) { report('学校 Logo 仅支持 JPG、JPEG、PNG 格式的图片。'); return; }
    if (file.size > 2 * 1024 * 1024) { report('学校 Logo 大小不能超过 2 MB。'); return; }
    report('');
    cancel();
    const job = Symbol('logo-read');
    active.current = job;
    setStatus('reading');
    const current = new FileReader();
    reader.current = current;
    const isCurrent = () => mounted.current && active.current === job;
    function rejectImage(message: string) {
      if (!isCurrent()) return;
      active.current = null; reader.current = null;
      setStatus(draft.current === saved.current ? 'idle' : 'failed');
      report(message);
    }
    current.onerror = () => rejectImage('图片读取失败，请重新选择。');
    current.onload = () => {
      if (!isCurrent()) return;
      if (typeof current.result !== 'string') { rejectImage('图片读取失败，请重新选择。'); return; }
      reader.current = null;
      const url = current.result;
      const image = new Image();
      image.onerror = () => rejectImage('无法识别这张图片，请选择有效的 JPG 或 PNG 图片。');
      image.onload = () => {
        if (!isCurrent()) return;
        if (image.naturalWidth <= 0 || image.naturalHeight <= 0) { rejectImage('无法识别这张图片，请重新选择。'); return; }
        const next = { url, width: image.naturalWidth, height: image.naturalHeight };
        draft.current = next;
        setAsset(next);
        void persist(next);
      };
      image.src = url;
    };
    current.readAsDataURL(file);
  }

  async function remove() {
    draft.current = null;
    setAsset(null);
    await persist(null);
  }

  const scale = asset ? Math.min(190 / asset.width, 90 / asset.height) : 1;
  return {
    preview: asset?.url ?? '', savedUrl: confirmed?.url ?? '', status,
    displaySize: asset ? { width: asset.width * scale, height: asset.height * scale } : { width: 190, height: 56.694 },
    select, retry: () => persist(draft.current), remove,
  };
}
